import { Stack, Card, Space, Group, Box } from "@mantine/core";
import {
  useIsDesktop,
  useIsLaptop,
  useIsLaptopOrBigger,
  useIsMobile,
  useStyles,
} from "@/styles/useStyles";
import logo from "@/assets/advize-logo.webp";
import { PartnerLogo } from "@/components/PartnerLogo";
import { useCallback, useMemo, useState } from "react";
import { AdvizerTag } from "./AdvizerTag";
import classes from "@/styles/Carousel.module.css";
import { IAdvizerModel } from "@/models/Advizer";
import { AdvizerVideoSlideshow } from "@/components/AdvizerVideoSlideshow";
import { Image } from "@/components/ui/Image";
import {
  useAdvizer,
  useAdvizerQuotes,
  useArrayOfSelectedAdvizerSearchFilters,
} from "@/store/selectors";
import { Text } from "@/components/ui/Text";
import { IconArrowRight, IconStarFilled } from "@tabler/icons-react";
import { AdvizerQuotes } from "./AdvizerQuotes";
import { useStore } from "@/store";
import { useShowMBA } from "@/store/selectors/featureSelectors";
import { Link } from "@/components/ui/Link";
import { config } from "@/config";
import { CopyPlaylistLink } from "./playlists/CopyPlaylistLink";
import { useIsRecommendedAdvizer } from "@/store/selectors/userProfileSelectors";
import { Badge } from "@/components/ui/Badge";

export function AdvizerVideoCard({
  loading,
  advizerId,
  isScrolling,
  advizer: advizerFromProps,
  maxWidth,
  simple,
  questionNumber,
  disableTags,
  hideVideo,
}: {
  id?: string;
  loading?: boolean;
  advizerId: string;
  isScrolling?: boolean;
  style?: React.CSSProperties;
  advizer?: IAdvizerModel;
  maxWidth?: number;
  simple?: boolean;
  questionNumber?: number;
  disableTags?: boolean;
  hideVideo?: boolean;
}) {
  const { theme } = useStyles();
  const isDesktop = useIsDesktop();
  const isLaptopOrBigger = useIsLaptopOrBigger();
  const isLaptop = useIsLaptop();
  const isMobile = useIsMobile();
  const advizer = useAdvizer(advizerId) || advizerFromProps;
  const showMBA = useShowMBA();
  const advizerIsMBA = advizer?.gradDegree === "MBA";
  // const quotes = advizer?.quotes;
  const quotes = useAdvizerQuotes(advizerId);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(
    Math.min(
      Math.max((advizer?.defaultQuestionNumber || 0) - 1, 0),
      (advizer?.videos.length || 0) - 1,
    ),
  );
  const activeQuestionId = useStore(
    (state) => state.advizerSearch.activeQuestionId,
  );
  const selectedAdvizerSearchFilters = useArrayOfSelectedAdvizerSearchFilters();
  const isRecommendedAdvizer = useIsRecommendedAdvizer({ advizerId });

  const activeVideo = useMemo(() => {
    if (activeQuestionId) {
      return advizer?.videos.find(
        (video) => video.question?.id === activeQuestionId,
      );
    }
    return advizer?.videos[currentSlideIndex];
  }, [advizer?.videos, currentSlideIndex, activeQuestionId]);

  const activeQuote = useMemo(() => {
    if (!quotes) {
      return undefined;
    }
    return quotes.find((quote) => quote.videoId === activeVideo?.id);
  }, [quotes, activeVideo]);

  const maxNumTags = isDesktop ? 12 : 8;
  const sortedTags = useMemo(() => {
    if (
      selectedAdvizerSearchFilters &&
      (advizer?.tags?.length || 0) > maxNumTags
    ) {
      return (advizer?.tags || []).sort((a) =>
        selectedAdvizerSearchFilters.some((filter) => filter.id === a.id)
          ? -1
          : 1,
      );
    }
    return advizer?.tags.slice(0, maxNumTags) || [];
  }, [advizer?.tags, maxNumTags, selectedAdvizerSearchFilters]);

  const advizerPartnersToShow = useMemo(() => {
    if (
      (advizer?.partners || []).some((partner) =>
        partner.emailFormat?.includes("arizona.edu"),
      )
    ) {
      const hasEller = (advizer?.partners || []).some((partner) =>
        partner.slug?.includes("eller"),
      );

      if (hasEller) {
        return (advizer?.partners || []).filter(
          (partner) => partner.slug !== "university-of-arizona",
        );
      }
    }

    return advizer?.partners || [];
  }, [advizer?.partners]);

  const renderAdvizerNameHeader = useCallback(() => {
    return (
      <Group>
        <Link
          to={`/advizers/${advizerId}`}
          style={{ flex: 1 }}
          keepExistingUrlParams
        >
          <Group gap={8} align="center" justify="space-between" w="100%">
            <Group gap={8} align="center" justify="flex-start">
              <Box
                bg="primary.9"
                p={1}
                bd="1px solid primary.1"
                style={{ borderRadius: "50%" }}
              >
                {isScrolling ? (
                  <Box w={40} h={40} bg="primary.5" />
                ) : (
                  <Image
                    alt={`Advizer ${advizer?.firstName}'s profile picture`}
                    width={40}
                    height={40}
                    radius="xl"
                    src={advizer?.profilePictureURL || logo}
                  />
                )}
              </Box>
              <Text titleStyle c="text.7" fw={600} lh={1} pt={1}>
                {advizer?.firstName}
              </Text>
              <Space w={4} />

              <Text
                size="sm"
                c="primary.7"
                fw={500}
                style={{ display: "flex", alignItems: "center" }}
              >
                {config.isPartnerApp ? "View" : "View Profile"}
                <IconArrowRight size={16} style={{ marginLeft: 4 }} />
              </Text>
            </Group>
            {isRecommendedAdvizer && (
              <Badge
                ariaLabel="Recommended Advizer"
                variant="light"
                color="primary"
                size="lg"
                leftSection={<IconStarFilled size={14} />}
                rightSection={<IconStarFilled size={14} />}
              >
                Recommended
              </Badge>
            )}
          </Group>
        </Link>

        {config.isPartnerApp && (
          <CopyPlaylistLink
            propsLink={`${window.location.origin}/advizers/${advizerId}`}
            text="Copy student share link"
            context="Advizer Video Card"
            size="sm"
          />
        )}

        {advizerPartnersToShow.length > 0 && (
          <Group justify="flex-end" px="xs">
            {advizerPartnersToShow.map((partner) => (
              <PartnerLogo
                key={partner.id}
                partnerId={partner.id}
                // src={partner.iconURL}
                width={40}
              />
            ))}
          </Group>
        )}
      </Group>
    );
  }, [
    advizer?.firstName,
    advizer?.profilePictureURL,
    advizerId,
    advizerPartnersToShow,
    isScrolling,
    isRecommendedAdvizer,
  ]);

  const renderAdvizerQuotes = useCallback(() => {
    return <AdvizerQuotes advizer={advizer} activeQuote={activeQuote} />;
  }, [activeQuote, advizer]);

  const renderVideos = useCallback(() => {
    if (!advizer) {
      return null;
    }
    return (
      <Card
        padding="md"
        radius="md"
        bg="transparent"
        w="100%"
        h="100%"
        flex={1}
      >
        <Card.Section pos="relative">
          <AdvizerVideoSlideshow
            classes={classes}
            advizer={advizer}
            quotes={quotes || []}
            currentSlideIndex={currentSlideIndex}
            setCurrentSlideIndex={setCurrentSlideIndex}
            questionNumber={questionNumber}
          />
        </Card.Section>
      </Card>
    );
  }, [advizer, currentSlideIndex, quotes, questionNumber]);

  const renderAdvizerTitle = useCallback(() => {
    return (
      <Link
        to={`/advizers/${advizerId}`}
        style={{ width: "100%" }}
        keepExistingUrlParams
      >
        <Text
          titleStyle
          fz={isLaptop ? 24 : isMobile ? 20 : 24}
          c="text.7"
          w="100%"
          fw={600}
          lh={1.2}
          py={2}
          px={4}
          ta="left"
          lineClamp={3}
        >
          <span style={{ fontWeight: 700 }}>{advizer?.jobTitle}</span>
          <span style={{ paddingLeft: 6, paddingRight: 8, opacity: 0.8 }}>
            @
          </span>
          <span style={{ opacity: 0.9 }}>{advizer?.company}</span>
        </Text>
      </Link>
    );
  }, [advizer?.company, advizer?.jobTitle, advizerId, isLaptop, isMobile]);

  const renderTags = useCallback(() => {
    return (
      <Stack gap="xs" w="100%">
        <Group w="100%" gap={isDesktop ? 12 : 8} px={0} py="xs">
          {showMBA && advizerIsMBA && (
            <AdvizerTag
              key="mba"
              tag={{ id: "MBA", title: "MBA", type: "degree" }}
              size={isLaptopOrBigger ? "md" : "md"}
              maw={300}
              index={0}
              disableClick
            />
          )}
          {sortedTags.map((tag, index) => (
            <AdvizerTag
              key={tag.id}
              tag={tag}
              size={isLaptopOrBigger ? "md" : "md"}
              maw={300}
              index={index}
              disableClick={disableTags}
            />
          ))}
        </Group>
      </Stack>
    );
  }, [
    advizerIsMBA,
    disableTags,
    isDesktop,
    isLaptopOrBigger,
    showMBA,
    sortedTags,
  ]);

  const renderContent = useCallback(() => {
    if (isLaptopOrBigger && !simple) {
      return (
        <Group align="start" justify="center" w="100%" p="md" wrap="nowrap">
          <Stack flex={1} gap={8} pt={4} w="100%">
            {renderAdvizerNameHeader()}
            <Space h={0} />
            {renderAdvizerTitle()}
            {renderAdvizerQuotes()}
            {renderTags()}
          </Stack>
          <Stack flex={1}>{renderVideos()}</Stack>
        </Group>
      );
    }
    return (
      <Stack
        w="100%"
        h="100%"
        maw={700}
        gap={0}
        bg={simple ? "background.0" : undefined}
        p={simple ? "xl" : undefined}
        style={{
          borderRadius: simple ? 20 : undefined,
          border: simple ? `1px solid ${theme.colors.border[2]}` : undefined,
          boxShadow: simple ? "0 0 3px 0 rgba(0, 0, 0, 0.1)" : undefined,
        }}
      >
        <Group w="100%">
          <Stack w="100%" ta="left" p={0} gap={0} h="100%">
            {renderAdvizerNameHeader()}
            <Space h="md" />
            {renderAdvizerTitle()}
            {!hideVideo && (
              <>
                <Space h="xs" />
                {renderAdvizerQuotes()}
                <Space h="sm" />
                {renderVideos()}
              </>
            )}
          </Stack>
        </Group>

        <Space h={hideVideo ? 0 : 12} />

        {renderTags()}
      </Stack>
    );
  }, [
    isLaptopOrBigger,
    simple,
    theme.colors.border,
    renderAdvizerNameHeader,
    renderAdvizerTitle,
    hideVideo,
    renderAdvizerQuotes,
    renderVideos,
    renderTags,
  ]);

  if (!advizer?.name && !loading) {
    return null;
  }

  // TODO auto pause/play if video is playing and user goes to next/previous
  return (
    <Stack
      align="center"
      w="100%"
      mx="auto"
      gap={0}
      py="md"
      px={simple ? undefined : "md"}
      maw={maxWidth}
      bg={simple ? undefined : "background.0"}
    >
      {renderContent()}
    </Stack>
  );
}
